import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Avatar,
  alpha,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Palette as PaletteIcon,
  Colorize as ColorizeIcon,
  Logout as LogoutIcon,
  ViewCompact as ViewCompactIcon,
  ViewComfy as ViewComfyIcon,
  Category as CategoryIcon,
  Security as SecurityIcon,
  Business as BusinessIcon,
  History as HistoryIcon,
  BrandingWatermark as BrandingIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getAppVersionSync } from '../../utils/versionUtils';
import ExpandableNav from './ExpandableNav';
import Changelog from '../changelog/Changelog';
import { StrawberryIcon, IconNames } from '../icons';

const getProfileImage = (user) => {
  if (!user) return null;
  // Handle Google profile images
  if (user.providerData?.[0]?.photoURL) {
    return user.providerData[0].photoURL;
  }
  // Fallback to user photoURL
  return user.photoURL;
};

const Header = ({ 
  user,
  darkMode,
  onDarkModeToggle,
  setSnackbar,
  onStatsOpen,
  onLogout,
  useColoredCategories,
  onToggleColoredCategories,
  onToggleCompactView,
  filters,
  sidebarCollapsed,
  onHotelDeselect,
  onBeforeNavClick
}) => {
  const navigate = useNavigate();
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [appVersion, setAppVersion] = useState('');
  const [changelogAnchor, setChangelogAnchor] = useState(null);


  // Get app version on component mount
  useEffect(() => {
    // Start with the sync version to avoid flicker
    setAppVersion(getAppVersionSync());
    
    // Try to fetch the version dynamically, which might be different
    // on deployed environments using version.json
    const fetchVersion = async () => {
      try {
        // Add cache busting parameter to prevent caching
        const response = await fetch(`/version.json?t=${new Date().getTime()}`);
        if (response.ok) {
          const data = await response.json();
          setAppVersion(data.version);
        }
      } catch (error) {
        // Keep using the sync version if fetch fails
        console.warn('Failed to fetch version.json:', error);
      }
    };
    
    fetchVersion();
  }, []);

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  // Add handler for changelog
  const handleChangelogOpen = (event) => {
    event.stopPropagation();
    setChangelogAnchor(event.currentTarget);
  };

  const handleChangelogClose = () => {
    setChangelogAnchor(null);
  };

  return (
    <>
      <Box sx={{ 
        position: 'fixed', // Fixed positioning to overlay the sidebar
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1200, // Above sidebar
        width: '100%',
        bgcolor: 'background.default', // Switched to default (now lighter)
        // Add subtle divider under header
        borderBottom: '1px solid',
        borderColor: theme => alpha(theme.palette.divider, 0.06),
        transition: 'all 0.3s ease',
        // Create visual connection with sidebar
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: { xs: '0px', md: '380px' }, // Updated to match refined sidebar width
          height: '100%',
          bgcolor: 'background.default', // Switched to default (now lighter)
          // Remove border for clean look
          zIndex: -1,
        }
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 3, // Increased gap for better breathing room
          minHeight: { xs: 70, sm: 85 }, // Refined height for better proportion
          px: { xs: 2.5, sm: 4 }, // Improved horizontal padding
          py: { xs: 1.5, sm: 2 },
          mx: 'auto',
          position: 'relative',
          // Add left margin on desktop to account for sidebar
          ml: { xs: 0, md: '380px' }, // Updated to match refined sidebar
          // Adjust width to account for sidebar
          width: { xs: '100%', md: 'calc(100% - 380px)' }, // Updated calculation
          bgcolor: 'background.default', // Match logo section color
          // Remove decorative gradient for clean minimal look
        }}>
          {/* Logo and Title - Only show on right side when desktop */}
          <Box sx={{ 
            display: { xs: 'flex', md: 'none' },
            alignItems: 'center', 
            gap: 1.5,
            // Hide on desktop since it will be in the sidebar
          }}>
            <Tooltip title="Created by Jesper Vestin" arrow placement="bottom">
              <Box 
                onClick={() => {
                  if (onHotelDeselect) {
                    onHotelDeselect();
                  }
                  // Only navigate if we're not already on the home page
                  if (window.location.pathname !== '/app') {
                    navigate('/app');
                  }
                }}
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  userSelect: 'none',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <StrawberryIcon 
                  name={IconNames.COPY}
                  size={24}
                  sx={{ 
                    color: 'primary.main'
                  }}
                />
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontFamily: 'StrawberrySansDisplay',
                    fontWeight: 500,
                    fontSize: { xs: '1.5rem', sm: '2.25rem' },
                    color: 'text.primary',
                }}
              >
                Hotel Manager
              </Typography>
              </Box>
            </Tooltip>
            <Tooltip title="Changelog" arrow placement="bottom">
              <Chip
                label={`v${appVersion}`}
                size="small"
                onClick={handleChangelogOpen}
                sx={{
                  height: { xs: 20, sm: 24 },
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  bgcolor: 'background.paper',
                  color: 'text.secondary',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              />
            </Tooltip>
          </Box>

          {/* Center content for desktop - page title */}
          <Box sx={{ 
            flex: 1,
            display: { xs: 'none', md: 'flex' },
            justifyContent: 'flex-start',
            alignItems: 'center',
            pl: 0,
          }}>
            {/* Page Title - Only visible when sidebar is expanded */}
            {!sidebarCollapsed && (
            <Typography 
              variant="h6" 
              sx={{ 
                fontFamily: 'StrawberrySansDisplay',
                fontWeight: 500,
                color: 'text.secondary',
                  fontSize: '1.1rem',
                  opacity: sidebarCollapsed ? 0 : 1,
                  transform: sidebarCollapsed ? 'translateY(10px)' : 'translateY(0)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              {/* This could show current page/hotel name */}
            </Typography>
            )}
          </Box>

          {/* Right Side Actions */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.5,
            ml: { xs: 'auto', md: 0 },
            '& .MuiButton-root, & .MuiIconButton-root': {
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-1px)',
                '& .MuiSvgIcon-root': {
                  transform: 'scale(1.1)',
                }
              },
              '& .MuiSvgIcon-root': {
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }
            }
          }}>
            <ExpandableNav user={user} onUserMenuOpen={handleUserMenuOpen} onBeforeNavClick={onBeforeNavClick} />
          </Box>
        </Box>

        {/* Sidebar Header Content - Only on desktop */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: { xs: '0px', md: '380px' }, // Updated to match refined sidebar width
          height: '100%',
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          px: 4, // Increased padding for better balance
          zIndex: 1,
          bgcolor: 'background.default', // Ensure consistent background color
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}> {/* Reduced gap */}
            <Tooltip title="Created by Jesper Vestin" arrow placement="bottom">
              <Box 
                onClick={() => {
                  if (onHotelDeselect) {
                    onHotelDeselect();
                  }
                  // Only navigate if we're not already on the home page
                  if (window.location.pathname !== '/app') {
                    navigate('/app');
                  }
                }}
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  userSelect: 'none',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <StrawberryIcon 
                  name={IconNames.COPY}
                  size={28}
                  sx={{ 
                    color: 'primary.main'
                  }}
                />
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontFamily: 'StrawberrySansDisplay',
                    fontWeight: 500,
                    fontSize: '1.8rem', // Larger for better visibility
                    color: 'text.primary',
                }}
              >
                Hotel Manager
              </Typography>
              </Box>
            </Tooltip>
            <Tooltip title="Changelog" arrow placement="bottom">
              <Chip
                label={`v${appVersion}`}
                size="small"
                onClick={handleChangelogOpen}
                sx={{
                  height: 22, // Slightly larger for better proportion
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  bgcolor: 'background.paper',
                  color: 'text.secondary',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              />
            </Tooltip>
          </Box>
        </Box>
      </Box>

        {/* User Menu */}
        <Menu
          anchorEl={userMenuAnchor}
          open={Boolean(userMenuAnchor)}
          onClose={handleUserMenuClose}
          onClick={handleUserMenuClose}
          slotProps={{
            paper: {
              elevation: 0,
              sx: {
                mt: 1.5,
                minWidth: 300,
                borderRadius: '16px',
                boxShadow: theme => `
                  0 4px 12px ${alpha(theme.palette.common.black, 0.06)},
                  0 1px 1px ${alpha(theme.palette.primary.main, 0.04)}
                `,
                border: '1px solid',
                borderColor: theme => alpha(theme.palette.divider, 0.08),
                overflow: 'visible',
                bgcolor: 'background.paper',
                backgroundImage: 'none',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -6,
                  left: 245,
                  width: 12,
                  height: 12,
                  bgcolor: 'background.paper',
                  transform: 'rotate(45deg)',
                  borderLeft: '1px solid',
                  borderTop: '1px solid',
                  borderColor: theme => alpha(theme.palette.divider, 0.08),
                  zIndex: 0,
                },
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1.5,
                  borderRadius: '12px',
                  mx: 1,
                  my: 0.5,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                    '& .MuiListItemIcon-root': {
                      color: 'primary.main',
                      transform: 'scale(1.1) translateX(2px)',
                    }
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'text.secondary',
                    minWidth: 36,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  },
                  '& .MuiTypography-root': {
                    fontSize: '0.9rem',
                    fontWeight: 500,
                  }
                }
              }
            }
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <Box>
            <Box sx={{ 
              px: 2, 
              py: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ position: 'relative' }}>
                  <Avatar 
                    src={getProfileImage(user)}
                    alt={user?.displayName || user?.email}
                    imgProps={{
                      referrerPolicy: "no-referrer"
                    }}
                    sx={{ 
                      width: 48,
                      height: 48,
                      boxShadow: theme => `0 0 0 4px ${alpha(theme.palette.primary.main, 0.2)}`,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      bgcolor: theme => alpha(theme.palette.primary.main, 0.12),
                      color: 'primary.main',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: theme => `0 0 0 4px ${alpha(theme.palette.primary.main, 0.3)}`,
                      }
                    }}
                  >
                    {!getProfileImage(user) && (user?.displayName?.charAt(0) || user?.email?.charAt(0) || 'G')}
                  </Avatar>
                  
                  {/* User Emoji in Menu */}
                  {user?.emoji && (
                    <Box 
                      sx={{
                        position: 'absolute',
                        bottom: -8,
                        right: -8,
                        fontSize: '1.25rem',
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    >
                      {user.emoji}
                    </Box>
                  )}
                </Box>
                
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: 'text.primary',
                      lineHeight: 1.2,
                      mb: 0.5,
                      fontFamily: 'StrawberrySansDisplay'
                    }}
                  >
                    {user?.displayName || 'Guest'}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                      fontFamily: 'StrawberrySansText'
                    }}
                  >
                    {user?.email}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, pl: 7 }}>
                <Chip
                  label={user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Guest'}
                  size="small"
                  sx={{ 
                    height: 28,
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.12),
                    color: 'primary.main',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    borderRadius: '8px',
                    border: '1px solid',
                    borderColor: theme => alpha(theme.palette.primary.main, 0.2),
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      bgcolor: theme => alpha(theme.palette.primary.main, 0.16),
                      transform: 'translateY(-1px)',
                      boxShadow: theme => `0 4px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                    },
                    '& .MuiChip-label': {
                      px: 1.5,
                      py: 0.5
                    }
                  }}
                />
              </Box>
            </Box>
            <Divider sx={{ 
              my: 1,
              borderColor: theme => alpha(theme.palette.divider, 0.08),
              mx: 2
            }} />

            <MenuItem onClick={onDarkModeToggle}>
              <ListItemIcon>
                {darkMode ? <LightModeIcon sx={{ fontSize: '1.25rem' }} /> : <DarkModeIcon sx={{ fontSize: '1.25rem' }} />}
              </ListItemIcon>
              {darkMode ? "Light Mode" : "Dark Mode"}
            </MenuItem>

            <MenuItem onClick={onToggleColoredCategories}>
              <ListItemIcon>
                <PaletteIcon sx={{ fontSize: '1.25rem' }} />
              </ListItemIcon>
              {useColoredCategories ? "Turn off Category Colors" : "Turn on Category Colors"}
            </MenuItem>
            
            <MenuItem onClick={onToggleCompactView}>
              <ListItemIcon>
                {user?.themePreferences?.compactView ? 
                  <ViewComfyIcon sx={{ fontSize: '1.25rem' }} /> : 
                  <ViewCompactIcon sx={{ fontSize: '1.25rem' }} />
                }
              </ListItemIcon>
              {user?.themePreferences?.compactView ? "Regular View" : "Compact View"}
            </MenuItem>
            
            <MenuItem onClick={() => navigate('/app/theme')}>
              <ListItemIcon>
                <ColorizeIcon sx={{ fontSize: '1.25rem' }} />
              </ListItemIcon>
              Theme Settings
            </MenuItem>

            {/* Admin Tools Section */}
            {user?.role === 'admin' && (
              <>
                <Divider sx={{ 
                  my: 1,
                  borderColor: theme => alpha(theme.palette.divider, 0.08),
                  mx: 2
                }} />
                
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      fontFamily: 'StrawberrySansText'
                    }}
                  >
                    Admin Tools
                  </Typography>
                </Box>

                <MenuItem onClick={() => navigate('/app/category-management')}>
                  <ListItemIcon>
                    <CategoryIcon sx={{ fontSize: '1.25rem' }} />
                  </ListItemIcon>
                  Category Management
                </MenuItem>

                <MenuItem onClick={() => navigate('/app/chain-management')}>
                  <ListItemIcon>
                    <BrandingIcon sx={{ fontSize: '1.25rem' }} />
                  </ListItemIcon>
                  Chain Management
                </MenuItem>

                <MenuItem onClick={() => navigate('/app/user-management')}>
                  <ListItemIcon>
                    <SecurityIcon sx={{ fontSize: '1.25rem' }} />
                  </ListItemIcon>
                  User Management
                </MenuItem>

                <MenuItem onClick={() => navigate('/hotels')}>
                  <ListItemIcon>
                    <BusinessIcon sx={{ fontSize: '1.25rem' }} />
                  </ListItemIcon>
                  Hotel Management
                </MenuItem>

                <MenuItem onClick={() => navigate('/app/activity-logs')}>
                  <ListItemIcon>
                    <HistoryIcon sx={{ fontSize: '1.25rem' }} />
                  </ListItemIcon>
                  Activity Logs
                </MenuItem>

                <MenuItem onClick={() => navigate('/app/force-refresh')}>
                  <ListItemIcon>
                    <RefreshIcon sx={{ fontSize: '1.25rem' }} />
                  </ListItemIcon>
                  Force Refresh
                </MenuItem>
              </>
            )}

            <Divider sx={{ 
              my: 1,
              borderColor: theme => alpha(theme.palette.divider, 0.08),
              mx: 2
            }} />

            <MenuItem 
              onClick={onLogout}
              sx={{
                color: '#FF4D4F !important',
                '& .MuiListItemIcon-root': {
                  color: '#FF4D4F !important'
                },
                '&:hover': {
                  bgcolor: `${alpha('#FF4D4F', 0.08)} !important`,
                }
              }}
            >
              <ListItemIcon>
                <LogoutIcon sx={{ fontSize: '1.25rem' }} />
              </ListItemIcon>
              Sign Out
            </MenuItem>
          </Box>
        </Menu>

      {/* Add Changelog component */}
      <Changelog
        anchorEl={changelogAnchor}
        onClose={handleChangelogClose}
      />
    </>
  );
};

export default Header; 